import { useEffect, useId, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { IconX } from '@tabler/icons-react';
import { Checkbox, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui';

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
  htmlFor: string;
}): JSX.Element {
  return (
    <div className={`field${error ? ' field--invalid' : ''}`}>
      <label className="field__label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hint && !error ? <p className="field__hint">{hint}</p> : null}
      {error ? (
        <p className="field__error" id={`${htmlFor}-error`} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function TextField({
  label,
  hint,
  value,
  onChange,
  placeholder,
  type = 'text',
  autoComplete,
  required,
  disabled,
  id,
}: {
  label: string;
  hint?: ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'email' | 'password' | 'url' | 'number';
  autoComplete?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
}): JSX.Element {
  const generated = useId();
  const fieldId = id ?? generated;
  return (
    <Field label={label} hint={hint} htmlFor={fieldId}>
      <Input
        id={fieldId}
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        disabled={disabled}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
      />
    </Field>
  );
}

export function TextArea({
  label,
  hint,
  value,
  onChange,
  rows = 10,
  placeholder,
  id,
  spellCheck = false,
}: {
  label: string;
  hint?: ReactNode;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  id?: string;
  spellCheck?: boolean;
}): JSX.Element {
  const generated = useId();
  const fieldId = id ?? generated;
  return (
    <Field label={label} hint={hint} htmlFor={fieldId}>
      <textarea
        id={fieldId}
        className="input input--area"
        rows={rows}
        value={value}
        placeholder={placeholder}
        spellCheck={spellCheck}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}

export function SelectField<T extends string>({
  label,
  hint,
  value,
  onChange,
  options,
  id,
}: {
  label: string;
  hint?: ReactNode;
  value: T;
  onChange: (value: T) => void;
  options: readonly { value: T; label: string }[];
  id?: string;
}): JSX.Element {
  const generated = useId();
  const fieldId = id ?? generated;
  return (
    <Field label={label} hint={hint} htmlFor={fieldId}>
      <Select value={value} onValueChange={(next) => onChange(next as T)}>
        <SelectTrigger id={fieldId} className="input--select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

export function CheckboxField({
  label,
  checked,
  onChange,
  hint,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
  disabled?: boolean;
}): JSX.Element {
  const id = useId();
  return (
    <div className="field field--inline">
      <Checkbox
        id={id}
        className="checkbox"
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onChange(next === true)}
      />
      <label htmlFor={id}>
        {label}
        {hint ? <span className="field__hint field__hint--inline"> {hint}</span> : null}
      </label>
    </div>
  );
}

/** Focus-trapping dialog. Escape and the backdrop both close it. */
export function Modal({
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}): JSX.Element {
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className={`modal-content${wide ? ' modal-content--wide' : ''}`}>
        <DialogHeader className="modal__header">
          <div className="modal__heading">
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription className="sr-only">Dialog content for {title}</DialogDescription>
          </div>
          <DialogClose className="modal__close" aria-label="Close dialog">
            <IconX size={17} stroke={1.7} aria-hidden="true" />
          </DialogClose>
        </DialogHeader>
        <div className="modal__body">{children}</div>
        {footer ? <DialogFooter className="modal__footer">{footer}</DialogFooter> : null}
      </DialogContent>
    </Dialog>
  );
}

export function ConfirmButton({
  onConfirm,
  label,
  confirmLabel,
  className = 'btn btn--danger',
  title,
  disabled = false,
}: {
  onConfirm: () => void;
  label: string;
  confirmLabel: string;
  className?: string;
  title?: string;
  disabled?: boolean;
}): JSX.Element {
  // `armed` decides the label, so it must be state: a ref would not re-render
  // and the confirmation would never appear: every Delete would look inert
  // and a fast double-click would destroy without confirming.
  const [armed, setArmed] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      if (timer.current !== undefined) window.clearTimeout(timer.current);
    },
    [],
  );

  return (
    <button
      type="button"
      className={className}
      title={title}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        if (armed) {
          setArmed(false);
          onConfirm();
          return;
        }
        setArmed(true);
        if (timer.current !== undefined) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setArmed(false), 4000);
      }}
    >
      {armed ? confirmLabel : label}
    </button>
  );
}